import { ApiActionType } from '@/enum/ApiActionType'
import { parseAlarmFlagsToBitmap } from '@/enum/GliderAlarmCode'
import { WobType } from '@/enum/WobType'
import { AloDataPoolItem } from '@/models/alo/AloDataPoolItem'
import { AloMission } from '@/models/alo/AloMission'
import { AloDbFleet } from '@/models/alodb/AloDbFleet'
import { AloDbGlider } from '@/models/alodb/AloDbGlider'
import { AloDbMission } from '@/models/alodb/AloDbMission'
import { AloDbMissionGlobal } from '@/models/alodb/AloDbMissionGlobal'
import { AloDbRoleMapping } from '@/models/alodb/AloDbRoleMapping'
import { AloDbUser } from '@/models/alodb/AloDbUser'
import { ConsoleRow } from '@/models/ui/ConsoleRow'
import { buildCoverageStateId } from '@/models/ui/CoverageUiState'
import { DebugItem } from '@/models/ui/DebugItem'
import { buildMissionPlanStateId } from '@/models/ui/MissionPlanUiState'
import { IWobAdminChangedConstParam, WobAdminChanged } from '@/models/wob/WobAdminChanged'
import { WobAisStatus } from '@/models/wob/WobAisStatus'
import { IWobCovProgressConstParam, WobCovProgress } from '@/models/wob/WobCovProgress'
import { WobGliderFile } from '@/models/wob/WobGliderFile'
import { WobGliderModConf } from '@/models/wob/WobGliderModConf'
import { WobGliderModEvent } from '@/models/wob/WobGliderModEvent'
import { wobGliderNmea } from '@/models/wob/WobGliderNmea'
import { WobLogbookChanged } from '@/models/wob/WobLogbookChanged'
import { IWobSimProgressConstParam, WobSimProgress } from '@/models/wob/WobSimProgress'
import { IRootState } from '@/store'
import types from '@/store/all-mutation-types'
import util from '@/utils/classToObject'
import Vue from 'vue'
import { ActionContext } from 'vuex'
import { ISocketState } from './state'

export class SocketAction {
  public [types.SOCKET_SENDMESSAGE](context: ActionContext<ISocketState, IRootState>, message: any) {
    Vue.prototype.$socket?.send(message)
  }

  // default handler called for all methods
  public [types.SOCKET_ONMESSAGE](context: ActionContext<ISocketState, IRootState>, message: any) {
    //si pas authentifié on ne traite pas les websockets
    if (!context.rootGetters.isAuthenticated) return

    const wobMessage = JSON.parse(message.data)

    //ajoute le debug
    if (context.rootGetters.debugEnable) context.dispatch(types.DEBUG_ADD_ITEMS, DebugItem.createFromWob(wobMessage))

    //Recupere le mission Id du message
    const missionId = wobMessage?.targetMisId || wobMessage?.misId
    const fleetId = wobMessage?.fleId
    if (missionId !== undefined && missionId !== null && wobMessage.wobType != WobType.WobIrisProcessingSimuprogress) {
      //on s'assure que la mission existe dans la liste des missions
      context.dispatch(types.MISSION_ENSURE_MISSION, missionId).then(() => {
        //traitement des messages necessitant la mission
        //on regarde le type de wobMessage afin de prendre les actions correspondante
        switch (wobMessage.wobType) {
          case WobType.WobGliderGdb: {
            //message a afficher sur la console
            context.dispatch(types.MISSION_ADD_CONSOLE_ROW, { missionId, row: ConsoleRow.createConsoleRow(wobMessage) })
            break
          }
          case WobType.WobGliderIrisGdb: {
            //message a afficher sur la console
            context.dispatch(types.MISSION_ADD_CONSOLE_ROW, { missionId, row: ConsoleRow.createConsoleRow(wobMessage) })
            break
          }
          case WobType.wobGliderNmea: {
            const wobNmea = new wobGliderNmea(wobMessage)
            context.dispatch(types.MISSION_UPDATE_NMEA, wobNmea)
            break
          }
          case WobType.WobGliderHang: {
            //gestion evenementielle des loupiotes
            context.dispatch(types.MISSION_UPDATE, {
              misId: wobMessage.misId,
              gliderWaitingMode: 'Disconnected',
            })
            break
          }
          /*case WobType.WobGliderConnect: {
            //gestion evenementielle des loupiotes
            break
          }*/
          case WobType.WobGliderNav:
          case WobType.WobGliderStatus: {
            //mise à jour global du tableau de la mission sur certains champs
            context.dispatch(types.MISSION_UPDATE, wobMessage)
            break
          }
          case WobType.WobGliderAlarm: {
            // Met à jour gliderRuntime.alarmCode en temps réel. Le WS porte des
            // flags string (GliderAlarmCode) ; on convertit les alarmes actives
            // NON acquittées (bloc 1) en bitmap numérique pour parité avec
            // gli_alr du snapshot REST. ⚠ Bloc 2 (alarmNoAckFlags2) non agrégé :
            // les bits se chevauchent avec le bloc 1, gli_alr étant un entier
            // unique = bloc 1.
            const alarmGliId = wobMessage?.gliId
            if (alarmGliId !== undefined && alarmGliId !== null) {
              const alarmCode = parseAlarmFlagsToBitmap(wobMessage?.alarmNoAckFlags)
              context.dispatch(types.GLIDER_RUNTIME_PATCH_ALARM, { gliId: alarmGliId, alarmCode })
            }
            break
          }
          case WobType.WobGliderFile: {
            //gestion de l'avancement des téléchargements
            context.dispatch(types.MISSION_UPDATE_DOWNLOAD_FILE_STATUS, new WobGliderFile(wobMessage))
            break
          }
          case WobType.WobLogbookChanged: {
            context.dispatch(types.MISSION_LOGBOOK_ITEM_REQUEST, {
              misId: missionId,
              logBookId: new WobLogbookChanged(wobMessage).logbookItem.lbkId,
            })
            break
          }
          case WobType.WobGliderModConf: {
            context.dispatch(types.GLIDER_MODCONF_UPDATE, new WobGliderModConf(wobMessage))
            //mise à jour du state du glider pour la mission si detail chargé et mission active
            const mission = context.rootGetters.missionsById[missionId] as AloMission
            if (mission && mission.misActifOn && mission._detailLoaded)
              context.dispatch(types.MISSION_IRIS_GLIDER_STATE_REQUEST, missionId)
            break
          }
          case WobType.WobGliderModEvent: {
            const wobEvent = new WobGliderModEvent(wobMessage)
            //console.log("WobGliderModEvent",wobEvent)
            if (wobEvent.modKey == 'MissionPlan' && wobEvent.eventKey == 'UPDATED') {
              const uiid = buildMissionPlanStateId(missionId, 'mission')
              //verifie que le mission plan a bien bougé
              if (context.rootGetters.uistatebyuiid(uiid)) {
                context.dispatch(types.MISSION_IRIS_MISSION_PLAN_REQUEST, missionId)
              }
            }
            //mise à jour du state du glider pour la mission si detail chargé et mission active
            const mission = context.rootGetters.missionsById[missionId] as AloMission
            if (mission && mission.misActifOn && mission._detailLoaded)
              context.dispatch(types.MISSION_IRIS_GLIDER_STATE_REQUEST, missionId)
            break
          }
          case WobType.WobIrisProcessingNewPicture: {
            //@ts-ignore
            delete window[`irisimgcache_${wobMessage.misId}_all`]

            wobMessage.dataCycleList.forEach((dataCycle: number) => {
              //@ts-ignore
              delete window[`irisimgcache_${wobMessage.misId}_${dataCycle}`]
            })
            context.rootState.mission.refreshPicture = !context.rootState.mission.refreshPicture
            break
          }
          case WobType.WobAisStatus: {
            const wobAis = new WobAisStatus(wobMessage)
            context.dispatch(types.MISSION_IRIS_AIS_STATUS_UPDATE, { id: missionId, module_status: wobAis.aisStatus })
            break
          }
        }
      })
    } else if (
      fleetId !== undefined &&
      fleetId !== null &&
      wobMessage.wobType != WobType.WobIrisProcessingSimuprogress
    ) {
      switch (wobMessage.wobType) {
        case WobType.WobLogbookChanged: {
          context.dispatch(types.UI_FLEET_LOGBOOK_ITEM_REQUEST, {
            fleId: fleetId,
            logBookId: new WobLogbookChanged(wobMessage).logbookItem.lbkId,
          })
          break
        }
        case WobType.WobFleetStatus: {
          const newIdx = context.rootState.fleet.detailFleets.findIndex(it => it.fleet.FLE_ID == fleetId)
          //mise à jour du state de la flotte si flotte chargée
          if (newIdx >= 0) {
            context.dispatch(types.ADMINFLEET_SELECT_DETAIL_REQUEST, fleetId)
          }
          break
        }
      }
    } else {
      //on regarde le type de wobMessage afin de prendre les actions correspondante
      switch (wobMessage.wobType) {
        case WobType.WobIrisGdb: {
          if (wobMessage?.gdbType != 'IrisProcessingPathprogress')
            //avertissement a afficher sur toutes les consoles ouvertes
            context.dispatch(types.MISSION_ADD_CONSOLE_ROW, { row: ConsoleRow.createConsoleRow(wobMessage) })
          break
        }
        case WobType.WobDatapoolChanged: {
          context.dispatch(types.DATAPOOL_UPDATE, {
            dataPool: new AloDataPoolItem(wobMessage.newItem),
            actionType: wobMessage.actionType as ApiActionType,
          })
          break
        }
        case WobType.WobAdminChanged: {
          const wobAdmin = wobMessage as IWobAdminChangedConstParam
          switch (wobAdmin.aloDbClassName) {
            case 'AloDbMission':
              const wobAdminMissionChanged = new WobAdminChanged(wobAdmin, AloDbMission.createAloDbMissionWithDetail)
              context.dispatch(types.ADMINMISSION_EXTERNAL_UPDATE, wobAdminMissionChanged)
              break
            case 'AloDbGlider':
              const wobGliderChanged = new WobAdminChanged(wobAdmin, AloDbGlider.createAloDbGliderWithDetail)
              context.dispatch(types.GLIDER_EXTERNAL_UPDATE, wobGliderChanged)
              break
            case 'AloDbUser':
              const wobUserChanged = new WobAdminChanged(wobAdmin, AloDbUser.createAloDbUserWithDetail)
              context.dispatch(types.USER_EXTERNAL_UPDATE, wobUserChanged)
              break
            case 'AloDbRoleMapping':
              const wobRoleMappingChanged = new WobAdminChanged(wobAdmin, AloDbRoleMapping.createAloDbRoleMapping)
              context.dispatch(types.USER_ROLEMAPPING_EXTERNAL_UPDATE, wobRoleMappingChanged)
              break
            case 'AloDbFleet':
              const wobFleetChanged = new WobAdminChanged(wobAdmin, AloDbFleet.createAloDbFleetWithDetail)
              context.dispatch(types.ADMINFLEET_EXTERNAL_UPDATE, wobFleetChanged)
              break
            case 'AloDbMissionGlobal':
              const wobMigChanged = new WobAdminChanged(wobAdmin, AloDbMissionGlobal.createAloDbMissionGlobalWithDetail)
              context.dispatch(types.MISSIONGLOBAL_EXTERNAL_UPDATE, wobMigChanged)
              break
          }
          break
        }
        case WobType.WobKeepAlive: {
          if (context.state.keepAliveTimoutId != 0) window.clearTimeout(context.state.keepAliveTimoutId)
          //Ligne de vie pour fermeture socket si perte reseau
          const keepAliveTimoutId = window.setTimeout(() => {
            context.commit(types.SOCKET_ONCLOSE)
            Vue.prototype.$socket.close()
          }, 45000)
          context.commit(types.SOCKET_ONKEEPALIVE, keepAliveTimoutId)
          break
        }
        case WobType.WobServiceBroker: {
          //Si on recoit un message de type WobServiceBroker on reload la page
          window.location.reload()
          break
        }
        //TODO deplacer dans l'autre section lorsque l'on aura le service getMission fonctionnel avec les VIM
        case WobType.WobIrisProcessingSimuprogress: {
          const woSimProgress = new WobSimProgress(wobMessage as IWobSimProgressConstParam)
          //console.log("WobIrisProcessingSimuprogress",woSimProgress)
          context.dispatch(types.MISSIONVIRTUAL_UPDATE_PROGRESSION, woSimProgress)
          context.dispatch(types.MISSION_ENDMISSION_UPDATE_PROGRESSION, woSimProgress)
          break
        }
        case WobType.WobIrisProcessingPathprogress:
        case WobType.WobIrisProcessingKeepFormation: {
          const woCovProgress = new WobCovProgress(wobMessage as IWobCovProgressConstParam)
          console.log('woCovProgress', woCovProgress, wobMessage)
          //S'assure que c'est bien un message de progression d'une coverageIteration
          if (woCovProgress?.covId) {
            context.dispatch(types.COVERAGEITERATION_SELECT_DETAIL_REQUEST, woCovProgress.covId).then(() => {
              const coverage_uiid = buildCoverageStateId(woCovProgress.migId, 'detail')
              context.rootGetters.coverageStateByUiid(coverage_uiid).selectedCoverageId = woCovProgress.covId
            })
          } else if (woCovProgress?.migId) {
            context.dispatch(types.MISSIONGLOBAL_SELECT_DETAIL_REQUEST, woCovProgress.migId)
          }
          break
        }
      }
    }
  }
}

export default util.classToObject(SocketAction)
